import React from "react";
import { Box, IconButton, Button, Typography, Slide } from "@mui/material";
import { Share as ShareIcon, ContentCopy, Close } from "@mui/icons-material";
import { motion } from "framer-motion";

interface DetailShareBarProps {
  show: boolean;
  onWhatsAppShare: () => void;
  onCopyLink: () => void;
  onClose: () => void;
}

/**
 * Sticky bottom share bar for detail screens
 * - WhatsApp share button
 * - Copy link button
 * - Close button
 * - Thumb-friendly bottom positioning
 */
export const DetailShareBar: React.FC<DetailShareBarProps> = ({
  show,
  onWhatsAppShare,
  onCopyLink,
  onClose,
}) => {
  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Box
        component={motion.div}
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          bgcolor: "white",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
          borderTopLeftRadius: { xs: 20, md: 24 },
          borderTopRightRadius: { xs: 20, md: 24 },
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            bgcolor: "grey.100",
            "&:hover": { bgcolor: "grey.200" },
          }}
        >
          <Close />
        </IconButton>

        {/* Share Label */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "text.secondary",
            display: { xs: "none", sm: "block" },
          }}
        >
          Share this
        </Typography>

        {/* Share Buttons - Flex grows to fill space */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexGrow: 1,
            justifyContent: "flex-end",
          }}
        >
          {/* Share Button */}
          <Button
            variant="contained"
            startIcon={<ShareIcon />}
            onClick={onWhatsAppShare}
            sx={{
              bgcolor: "#25D366",
              color: "white",
              flexGrow: { xs: 1, sm: 0 },
              py: 1.5,
              px: { xs: 2, sm: 3 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                bgcolor: "#128C7E",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(37,211,102,0.3)",
              },
              transition: "all 0.2s",
            }}
          >
            Share
          </Button>

          {/* Copy Link Button */}
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={onCopyLink}
            sx={{
              flexGrow: { xs: 1, sm: 0 },
              py: 1.5,
              px: { xs: 2, sm: 3 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: 600,
              borderRadius: 2,
              borderWidth: 2,
              textTransform: "none",
              "&:hover": {
                borderWidth: 2,
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
              },
              transition: "all 0.2s",
            }}
          >
            Copy Link
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};
